import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useParams, useSearchParams } from 'react-router';

import { Button } from '@actual-app/components/button';
import { SvgSplit } from '@actual-app/components/icons/v0';
import {
  SvgCalendar,
  SvgCheveronDown,
  SvgLocation,
  SvgPiggyBank,
  SvgTag,
  SvgTrash,
  SvgUser,
  SvgWallet,
} from '@actual-app/components/icons/v1';
import {
  SvgCheckCircle1,
  SvgNotesPaper,
} from '@actual-app/components/icons/v2';
import { styles } from '@actual-app/components/styles';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { Toggle } from '@actual-app/components/toggle';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import { DEFAULT_MAX_DISTANCE_METERS } from '@actual-app/core/shared/constants';
import { getCurrency } from '@actual-app/core/shared/currencies';
import { calculateDistance } from '@actual-app/core/shared/location-utils';
import * as monthUtils from '@actual-app/core/shared/months';
import { q } from '@actual-app/core/shared/query';
import {
  addSplitTransaction,
  deleteTransaction,
  makeEmptySplitSubtransactions,
  realizeTempTransactions,
  splitTransaction,
  ungroupTransactions,
  updateTransaction,
} from '@actual-app/core/shared/transactions';
import {
  amountToInteger,
  applyFindReplace,
  diffItems,
  getChangedValues,
  groupById,
  integerToAmount,
  titleFirst,
} from '@actual-app/core/shared/util';
import type {
  AccountEntity,
  CategoryEntity,
  PayeeEntity,
  TransactionEntity,
} from '@actual-app/core/types/models';
import {
  format as formatDate,
  isValid as isValidDate,
  parse as parseDate,
  parseISO,
} from 'date-fns';

import { NoteInsertHashButton } from '#components/autocomplete/NoteInsertHashButton';
import { NoteTagAutocomplete } from '#components/autocomplete/NoteTagAutocomplete';
import { AyudaGavi } from '#components/mobile/ayuda/AyudaGavi';
import { OjoBuey } from '#components/mobile/ayuda/OjoBuey';
import { usePreguntasDetalleMovimiento } from '#components/mobile/ayuda/usePreguntasDetalleMovimiento';
import {
  conEtiquetaNoIngreso,
  tieneEtiquetaNoIngreso,
} from '#components/mobile/informes/noIngreso';
import { useColoresCategorias } from '#components/mobile/informes/useColoresCategorias';
import { MobileBackButton } from '#components/mobile/MobileBackButton';
import {
  CajaIconoFila,
  EtiquetaFila,
  FieldLabel,
  filaFormularioClassName,
  InputField,
  TapField,
  tarjetaFormularioStyle,
  ToggleField,
} from '#components/mobile/MobileForms';
import { comprobarPlan } from '#components/mobile/plan/cerrojoPlan';
import { puedeAprobarse } from '#components/mobile/revisar/revisar';
import { useAvisoSalida } from '#components/mobile/ui/avisoSalida';
import { Boton } from '#components/mobile/ui/Boton';
import { Icono } from '#components/mobile/ui/Icono';
import { Pildora } from '#components/mobile/ui/Pildora';
import { PildoraCategoria } from '#components/mobile/ui/PildoraCategoria';
import { Segmentado } from '#components/mobile/ui/Segmentado';
import { color, densidad, radio, sombra } from '#components/mobile/ui/tokens';
import { getPrettyPayee } from '#components/mobile/utils';
import { MobilePageHeader, Page } from '#components/Page';
import { shouldApplyRuleChange } from '#components/transactions/table/utils';
import { useAccounts } from '#hooks/useAccounts';
import { useCategories } from '#hooks/useCategories';
import { useDateFormat } from '#hooks/useDateFormat';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useLocationPermission } from '#hooks/useLocationPermission';
import { useNavigate } from '#hooks/useNavigate';
import { useNearbyPayees } from '#hooks/useNearbyPayees';
import { usePayees } from '#hooks/usePayees';
import {
  SingleActiveEditFormProvider,
  useSingleActiveEditForm,
} from '#hooks/useSingleActiveEditForm';
import { useSyncedPref } from '#hooks/useSyncedPref';
import { pushModal } from '#modals/modalsSlice';
import { addNotification } from '#notifications/notificationsSlice';
import { useSavePayeeLocationMutation } from '#payees';
import { locationService } from '#payees/location';
import { aqlQuery } from '#queries/aqlQuery';
import { useDispatch, useSelector } from '#redux';
import { setLastTransaction } from '#transactions/transactionsSlice';
import { registrarUso } from '#uso';
import { getStatusLabel } from '#util/schedule';

import { AmountInput } from './AmountInput';
import { categoriaHabitual } from './categoriaHabitual';
import { fechaLegible } from './fechaLegible';
import { fechaPorDefectoNuevoMovimiento } from './fechaPorDefecto';
import {
  cuentaPorDefecto,
  estadoPie,
  guardarUltimaCuenta,
  leerUltimaCuenta,
} from './pieEditor';
import type { EstadoPie } from './pieEditor';
import { categoriaIngreso as elegirCategoriaIngreso } from './seleccionCategoria';
import { SplitAmountInput } from './SplitAmountInput';

function getFieldName(transactionId: TransactionEntity['id'], field: string) {
  return `${field}-${transactionId}`;
}

function serializeTransaction(
  transaction: TransactionEntity,
  dateFormat: string,
) {
  const { date, amount } = transaction;
  return {
    ...transaction,
    date: formatDate(parseISO(date), dateFormat),
    amount: integerToAmount(amount || 0),
  };
}

function deserializeTransaction(
  transaction: TransactionEntity,
  originalTransaction: TransactionEntity | null,
  dateFormat: string,
) {
  const { amount, date: originalDate, ...realTransaction } = transaction;

  const dayMonth = monthUtils.getDayMonthRegex(dateFormat);
  let date: string | null = originalDate;
  if (dayMonth.test(date)) {
    const test = parseDate(
      date,
      monthUtils.getDayMonthFormat(dateFormat),
      new Date(),
    );
    if (isValidDate(test)) {
      date = monthUtils.dayFromDate(test);
    } else {
      date = null;
    }
  } else {
    const test = parseDate(date, dateFormat, new Date());
    // This is a quick sanity check to make sure something invalid
    // like "year 201" was entered
    if (test.getFullYear() > 2000 && isValidDate(test)) {
      date = monthUtils.dayFromDate(test);
    } else {
      date = null;
    }
  }

  if (date == null) {
    date =
      (originalTransaction && originalTransaction.date) ||
      monthUtils.currentDay();
  }

  return { ...realTransaction, date, amount: amountToInteger(amount || 0) };
}

export function lookupName(items: CategoryEntity[], id?: CategoryEntity['id']) {
  if (!id) {
    return null;
  }
  return items.find(item => item.id === id)?.name;
}

const dropdownChevron = (
  <SvgCheveronDown
    width={14}
    height={14}
    style={{ color: theme.pageTextSubdued, marginRight: 8 }}
  />
);

export function Status({
  status,
  isSplit = false,
}: {
  status?: string;
  isSplit?: boolean;
}) {
  const { t } = useTranslation();

  let color: string | undefined;

  switch (status) {
    case 'missed':
      color = theme.errorText;
      break;
    case 'due':
      color = theme.warningText;
      break;
    case 'upcoming':
      color = theme.tableHeaderText;
      break;
    default:
      return null;
  }

  return (
    <Text
      style={{
        fontSize: 11,
        color,
        fontStyle: 'italic',
        textAlign: 'left',
      }}
    >
      {isSplit
        ? t('{{status}} (Split)', {
            status: titleFirst(getStatusLabel(status)),
          })
        : titleFirst(getStatusLabel(status))}
    </Text>
  );
}

export type TipoMovimiento = 'gasto' | 'ingreso' | 'traspaso';

type SelectorTipoProps = {
  tipo: TipoMovimiento;
  isDisabled?: boolean;
  onChange: (tipo: TipoMovimiento) => void;
};

/** B8 · H12: el tipo tiñe solo el texto de la opción activa. */
const TINTE_TIPO: Record<TipoMovimiento, string> = {
  gasto: color.bad,
  ingreso: color.ok,
  traspaso: color.accent,
};

/** Selector «Gasto · Ingreso · Traspaso» bajo el importe (como YNAB). */
function SelectorTipo({ tipo, isDisabled, onChange }: SelectorTipoProps) {
  const { t } = useTranslation();
  return (
    <Segmentado<TipoMovimiento>
      etiqueta={t('Transaction type')}
      opciones={[
        {
          valor: 'gasto',
          texto: t('Spending (sign)'),
          tinte: TINTE_TIPO.gasto,
        },
        {
          valor: 'ingreso',
          texto: t('Income (sign)'),
          tinte: TINTE_TIPO.ingreso,
        },
        {
          valor: 'traspaso',
          texto: t('Transfer (type)'),
          tinte: TINTE_TIPO.traspaso,
        },
      ]}
      valor={tipo}
      onChange={onChange}
      testId="selector"
      isDisabled={isDisabled}
      estirar
      alto={38}
    />
  );
}

/** Fila «Desde» / «Hacia» de un traspaso (48 px, tocable). */
function FilaCuentaTraspaso({
  etiqueta,
  cuenta,
  isDisabled,
  onPress,
  testId,
}: {
  etiqueta: string;
  cuenta?: AccountEntity;
  isDisabled?: boolean;
  onPress: () => void;
  testId: string;
}) {
  const { t } = useTranslation();
  return (
    <Button
      variant="bare"
      bounce={false}
      isDisabled={isDisabled}
      onPress={onPress}
      data-testid={testId}
      className={filaFormularioClassName}
    >
      <CajaIconoFila>
        {cuenta?.offbudget ? (
          <SvgPiggyBank width={15} height={15} />
        ) : (
          <SvgWallet width={15} height={15} />
        )}
      </CajaIconoFila>
      <EtiquetaFila>{etiqueta}</EtiquetaFila>
      <View style={{ flex: 1, minWidth: 0, alignItems: 'flex-end' }}>
        <Text
          style={{
            ...densidad.nombre,
            color: cuenta ? color.fg : color.fg3,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {cuenta?.name ?? t('Select an account')}
        </Text>
        {!!cuenta?.offbudget && (
          <Text style={{ ...densidad.pequeno, color: color.fg3 }}>
            <Trans>Off budget</Trans>
          </Text>
        )}
      </View>
      {!isDisabled && dropdownChevron}
    </Button>
  );
}

const botonUbicacion = {
  backgroundColor: theme.buttonNormalBackground,
  border: `1px solid ${theme.buttonNormalBorder}`,
  color: theme.buttonNormalText,
  fontSize: '11px',
  padding: '4px 8px',
  borderRadius: 3,
  height: 'auto',
  minHeight: 'auto',
} as const;

const botonSecundario = {
  height: 44,
  padding: '0 14px',
  borderWidth: 0,
  borderRadius: radio.boton,
  backgroundColor: 'transparent',
  color: color.fg3,
  fontSize: 13,
  fontWeight: 600,
} as const;

/** Fila con interruptor (48 px) dentro de una tarjeta de formulario. */
function FilaInterruptor({
  icono,
  etiqueta,
  detalle,
  children,
  testId,
}: {
  icono: ReactNode;
  etiqueta: string;
  detalle?: string;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <View
      data-testid={testId}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        minHeight: 48,
        padding: '6px 14px',
      }}
    >
      <CajaIconoFila>{icono}</CajaIconoFila>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ fontSize: 13, fontWeight: 600, color: color.fg3 }}>
          {etiqueta}
        </Text>
        {detalle && (
          <Text
            style={{ ...densidad.pequeno, fontWeight: 500, color: color.fg3 }}
          >
            {detalle}
          </Text>
        )}
      </View>
      {children}
    </View>
  );
}

type FooterProps = {
  transactions: TransactionEntity[];
  estado: EstadoPie;
  onPrincipal: () => void;
  onSplit: (id: TransactionEntity['id']) => void;
  onAddSplit: (id: TransactionEntity['id']) => void;
  onEmptySplitFound: (id: TransactionEntity['id']) => void;
  editingField?: string;
};

function Footer({
  transactions,
  estado,
  onPrincipal,
  onSplit,
  onAddSplit,
  onEmptySplitFound,
  editingField,
}: FooterProps) {
  const { t } = useTranslation();
  const format = useFormat();
  const [transaction, ...childTransactions] = transactions;
  const emptySplitTransaction = childTransactions.find(t => t.amount === 0);
  const onClickRemainingSplit = () => {
    if (childTransactions.length === 0) {
      onSplit(transaction.id);
    } else {
      if (!emptySplitTransaction) {
        onAddSplit(transaction.id);
      } else {
        onEmptySplitFound?.(emptySplitTransaction.id);
      }
    }
  };

  return (
    <View
      data-testid="transaction-form-footer"
      style={{
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 10,
        paddingBottom: 'calc(10px + env(safe-area-inset-bottom))',
        background: `linear-gradient(to top, ${color.bg} 70%, transparent)`,
        gap: 8,
        '& button': {
          minHeight: 48,
          height: 48,
          borderRadius: radio.boton,
          fontWeight: 700,
          fontSize: 15,
          border: 0,
        },
        '& button span': { fontWeight: 700, fontSize: 15 },
      }}
    >
      {transaction.error?.type === 'SplitTransactionError' ? (
        <Button
          data-ayuda="pie-dividir"
          variant="primary"
          style={{ height: styles.mobileMinHeight }}
          isDisabled={!!editingField}
          onPress={onClickRemainingSplit}
        >
          <SvgSplit width={17} height={17} />
          <Text
            style={{
              ...styles.text,
              marginLeft: 6,
            }}
          >
            {!emptySplitTransaction ? (
              <Trans>
                Add new split -{' '}
                {{
                  amount: format(
                    transaction.amount > 0
                      ? transaction.error.difference
                      : -transaction.error.difference,
                    'financial',
                  ),
                }}{' '}
                left
              </Trans>
            ) : (
              <Trans>
                Amount left:{' '}
                {{
                  amount: format(
                    transaction.amount > 0
                      ? transaction.error.difference
                      : -transaction.error.difference,
                    'financial',
                  ),
                }}
              </Trans>
            )}
          </Text>
        </Button>
      ) : (
        <>
          {(estado.motivo || estado.avisoSinCategoria) && (
            <Text
              data-testid="pie-motivo"
              role="status"
              style={{
                textAlign: 'center',
                fontSize: 13,
                fontWeight: 700,
                color: estado.motivo ? color.fg2 : color.warn,
              }}
            >
              {estado.motivo === 'cuenta'
                ? t('Account is missing')
                : estado.motivo === 'importe'
                  ? t('Amount is missing')
                  : t('Give it a category before approving it')}
            </Text>
          )}
          <Boton
            bloque
            data-testid="pie-principal"
            data-accion={estado.accion}
            isDisabled={!!editingField || !!estado.motivo}
            onPress={onPrincipal}
          >
            {estado.accion === 'guardarYAprobar' && (
              <Icono nombre="check" size={18} />
            )}
            {estado.accion === 'anadir' ? (
              <Trans>Add transaction</Trans>
            ) : estado.accion === 'guardarYAprobar' ? (
              <Trans>Save and approve</Trans>
            ) : (
              <Trans>Save</Trans>
            )}
          </Boton>
        </>
      )}
    </View>
  );
}

type ChildTransactionEditProps = {
  keyboardHeader: ReactNode;
  transaction: TransactionEntity;
  negate: boolean;
  amountFocused: boolean;
  getCategory: (transaction: TransactionEntity, isOffBudget: boolean) => string;
  getPayee: (transaction: TransactionEntity) => PayeeEntity | undefined;
  getTransferAccount: (
    transaction: TransactionEntity,
  ) => AccountEntity | undefined;
  isOffBudget: boolean;
  isBudgetTransfer: (transaction: TransactionEntity) => boolean;
  onEditField: (
    id: TransactionEntity['id'],
    field: 'category' | 'payee' | 'account' | 'date' | 'amount' | 'notes',
  ) => void;
  onUpdate: <Field extends keyof TransactionEntity>(
    transaction: TransactionEntity,
    field: Field,
    value: TransactionEntity[Field],
  ) => void;
  onDelete: (id: TransactionEntity['id']) => void;
};

const ChildTransactionEdit = forwardRef<
  HTMLDivElement,
  ChildTransactionEditProps
>(
  (
    {
      transaction,
      negate,
      amountFocused,
      getCategory,
      getPayee,
      getTransferAccount,
      isOffBudget,
      isBudgetTransfer,
      onEditField,
      onUpdate,
      onDelete,
      keyboardHeader,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const { editingField, onRequestActiveEdit, onClearActiveEdit } =
      useSingleActiveEditForm()!;
    const noteRef = useRef<HTMLInputElement | null>(null);

    const prettyPayee = getPrettyPayee({
      t,
      transaction,
      payee: getPayee(transaction),
      transferAccount: getTransferAccount(transaction),
    });
    return (
      <View
        innerRef={ref}
        data-ayuda="parte"
        style={{
          backgroundColor: theme.tableBackground,
          borderColor:
            transaction.amount === 0
              ? theme.tableBorderSelected
              : theme.tableBorder,
          borderWidth: '1px',
          borderRadius: '5px',
          padding: '5px',
          margin: '10px',
        }}
      >
        <View style={{ flexDirection: 'row' }}>
          <View style={{ flexBasis: '75%' }}>
            <FieldLabel title={t('Payee')} />
            <TapField
              icon={<SvgUser width={17} height={17} />}
              placeholder={
                transaction.amount > 0
                  ? t('Who paid you?')
                  : t('Who did you pay?')
              }
              rightContent={dropdownChevron}
              isDisabled={
                !!editingField &&
                editingField !== getFieldName(transaction.id, 'payee')
              }
              value={prettyPayee}
              onPress={() => onEditField(transaction.id, 'payee')}
              data-testid={`payee-field-${transaction.id}`}
            />
          </View>
          <View
            data-ayuda="parte-importe"
            style={{
              flexBasis: '25%',
            }}
          >
            <FieldLabel title={t('Amount')} style={{ padding: 0 }} />
            <SplitAmountInput
              keyboardHeader={keyboardHeader}
              disabled={
                !!editingField &&
                editingField !== getFieldName(transaction.id, 'amount')
              }
              value={transaction.amount}
              negate={negate}
              autoFocus={amountFocused}
              onFocus={event => {
                onRequestActiveEdit(getFieldName(transaction.id, 'amount'));

                const input = event.currentTarget as HTMLInputElement;
                input.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              onBlur={() => onClearActiveEdit()}
              onChange={amount => {
                onUpdate(transaction, 'amount', amount);
              }}
            />
          </View>
        </View>

        <View>
          <FieldLabel title={t('Category')} />
          <TapField
            icon={<SvgTag width={17} height={17} />}
            placeholder={t('Select a category')}
            rightContent={dropdownChevron}
            textStyle={{
              ...((isOffBudget || isBudgetTransfer(transaction)) && {
                fontStyle: 'italic',
                color: theme.pageTextSubdued,
                fontWeight: 300,
              }),
            }}
            value={getCategory(transaction, isOffBudget)}
            isDisabled={
              (!!editingField &&
                editingField !== getFieldName(transaction.id, 'category')) ||
              isOffBudget ||
              isBudgetTransfer(transaction)
            }
            onPress={() => onEditField(transaction.id, 'category')}
            data-testid={`category-field-${transaction.id}`}
          />
        </View>

        <View>
          <FieldLabel title={t('Notes')} />
          <InputField
            ref={noteRef}
            iconStart={<SvgNotesPaper width={17} height={17} />}
            iconEnd={<NoteInsertHashButton inputRef={noteRef} />}
            placeholder={t('Add a note (optional)')}
            disabled={
              !!editingField &&
              editingField !== getFieldName(transaction.id, 'notes')
            }
            defaultValue={transaction.notes}
            onFocus={() =>
              onRequestActiveEdit(getFieldName(transaction.id, 'notes'))
            }
            onUpdate={value => onUpdate(transaction, 'notes', value)}
          />
          <NoteTagAutocomplete inputRef={noteRef} />
        </View>

        <View style={{ alignItems: 'center' }}>
          <Button
            variant="bare"
            onPress={() => onDelete(transaction.id)}
            style={{
              height: 40,
              borderWidth: 0,
              marginLeft: styles.mobileEditingPadding,
              marginRight: styles.mobileEditingPadding,
              marginTop: 10,
              backgroundColor: 'transparent',
            }}
          >
            <SvgTrash
              width={17}
              height={17}
              style={{ color: theme.errorText }}
            />
            <Text
              style={{
                color: theme.errorText,
                marginLeft: 5,
                userSelect: 'none',
              }}
            >
              <Trans>Delete split</Trans>
            </Text>
          </Button>
        </View>
      </View>
    );
  },
);

ChildTransactionEdit.displayName = 'ChildTransactionEdit';

type TransactionEditInnerProps = {
  isAdding: boolean;
  accounts: AccountEntity[];
  categories: CategoryEntity[];
  payees: PayeeEntity[];
  dateFormat: string;
  transactions: TransactionEntity[];
  onSave: (transactions: TransactionEntity[]) => void | Promise<void>;
  onUpdate: <Field extends keyof TransactionEntity>(
    transaction: TransactionEntity,
    field: Field,
    value?: TransactionEntity[Field],
  ) => void;
  onDelete: (id: TransactionEntity['id']) => void;
  onSplit: (id: TransactionEntity['id']) => void;
  onAddSplit: (id: TransactionEntity['id']) => void;
  shouldShowSaveLocation?: boolean;
  onSaveLocation?: () => void;
  onSelectNearestPayee?: () => void;
  onRequestLocation?: () => void;
  nearestPayee?: PayeeEntity | null;
  /** «Por revisar»: «Aprobar» flotante (guarda, aprueba y vuelve a la lista). */
  revisar?: { onAprobado: () => void } | null;
  /** Salir a propósito (guardar, borrar) sin preguntar «¿Descartar?». */
  salir?: (accion: () => void) => void;
};

const TransactionEditInner = memo<TransactionEditInnerProps>(
  function TransactionEditInner({
    isAdding,
    accounts,
    categories,
    payees,
    dateFormat,
    transactions: unserializedTransactions,
    onSave,
    onUpdate,
    onDelete,
    onSplit,
    onAddSplit,
    shouldShowSaveLocation,
    onSaveLocation,
    onSelectNearestPayee,
    onRequestLocation,
    nearestPayee,
    revisar,
    salir = accion => accion(),
  }) {
    const { t } = useTranslation();
    const locale = useLocale();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const transactions = useMemo(
      () =>
        unserializedTransactions.map(t =>
          serializeTransaction(t, dateFormat),
        ) || [],
      [unserializedTransactions, dateFormat],
    );
    const { data: { grouped: categoryGroups } = { grouped: [] } } =
      useCategories();
    const noteRef = useRef<HTMLInputElement | null>(null);

    const [transaction, ...childTransactions] = transactions;

    const { editingField, onRequestActiveEdit, onClearActiveEdit } =
      useSingleActiveEditForm()!;
    // Mientras se escribe el importe, los selectores (cuenta, categoría,
    // fecha…) siguen tocables: el importe se guarda solo al salir de él, y
    // si no el primer toque se perdía. Guardar y Gasto/Ingreso sí esperan.
    const bloqueaSelectores =
      editingField === getFieldName(transaction.id, 'amount')
        ? undefined
        : editingField;
    const childTransactionElementRefMap = useRef<
      Record<TransactionEntity['id'], HTMLDivElement | null>
    >({});
    const hasAccountChanged = useRef(false);

    const payeesById = useMemo(() => groupById(payees), [payees]);
    const accountsById = useMemo(() => groupById(accounts), [accounts]);

    const getAccount = useCallback(
      (trans: TransactionEntity) => {
        return trans?.account ? accountsById?.[trans.account] : undefined;
      },
      [accountsById],
    );

    const getPayee = useCallback(
      (trans: TransactionEntity) => {
        return trans?.payee ? payeesById?.[trans.payee] : undefined;
      },
      [payeesById],
    );

    const getTransferAccount = useCallback(
      (trans: TransactionEntity) => {
        const payee = trans ? getPayee(trans) : null;
        return payee && payee?.transfer_acct
          ? accountsById?.[payee.transfer_acct]
          : undefined;
      },
      [accountsById, getPayee],
    );

    const isBudgetTransfer = useCallback(
      (trans: TransactionEntity) => {
        const transferAcct = trans ? getTransferAccount(trans) : null;
        return transferAcct ? !transferAcct.offbudget : false;
      },
      [getTransferAccount],
    );

    const getCategory = useCallback(
      (trans: TransactionEntity, isOffBudget: boolean) => {
        if (isOffBudget) {
          return t('Off budget');
        } else if (isBudgetTransfer(trans)) {
          return t('Transfer (type)');
        } else {
          return lookupName(categories, trans.category) ?? '';
        }
      },
      [categories, isBudgetTransfer, t],
    );

    const onSaveInner = useCallback(async () => {
      const onConfirmSave = () => {
        let transactionsToSave = unserializedTransactions;
        if (isAdding) {
          transactionsToSave = realizeTempTransactions(
            unserializedTransactions,
          );
          // H67: el próximo gasto a mano empieza en esta cuenta.
          guardarUltimaCuenta(unserializedTransactions[0]?.account);
        }

        // Datos de uso: alta o edición y cuántas partes (nunca importes).
        registrarUso('movimiento', {
          accion: isAdding ? 'anadir' : 'editar',
          n: transactionsToSave.length,
        });
        void onSave(transactionsToSave);
        salir(() => void navigate(-1));
      };

      if (unserializedTransactions.some(t => t.reconciled)) {
        // On mobile any save gives the warning.
        // On the web only certain changes trigger a warning.
        // Should we bring that here as well? Or does the nature of the editing form
        // make this more appropriate?
        dispatch(
          pushModal({
            modal: {
              name: 'confirm-transaction-edit',
              options: {
                onConfirm: onConfirmSave,
                confirmReason: 'editReconciled',
              },
            },
          }),
        );
      } else {
        const transferIds = unserializedTransactions
          .map(t => t.transfer_id)
          .filter((id): id is string => id != null);

        if (transferIds.length > 0) {
          const { data } = await aqlQuery(
            q('transactions')
              .filter({
                id: { $oneof: transferIds },
                reconciled: true,
              })
              .select('id'),
          );
          if ((data as TransactionEntity[]).length > 0) {
            dispatch(
              pushModal({
                modal: {
                  name: 'confirm-transaction-edit',
                  options: {
                    onConfirm: onConfirmSave,
                    confirmReason: 'batchEditWithReconciledTransfer',
                  },
                },
              }),
            );
          } else {
            onConfirmSave();
          }
        } else {
          onConfirmSave();
        }
      }
    }, [
      isAdding,
      dispatch,
      navigate,
      onSave,
      unserializedTransactions,
      t,
      salir,
    ]);

    const onUpdateInner = useCallback(
      async <Field extends keyof TransactionEntity>(
        serializedTransaction: TransactionEntity,
        name: Field,
        value: TransactionEntity[Field],
      ) => {
        const newTransaction = { ...serializedTransaction, [name]: value };
        onUpdate(newTransaction, name);
        onClearActiveEdit();

        if (name === 'account') {
          hasAccountChanged.current = serializedTransaction.account !== value;
        }
      },
      [onClearActiveEdit, onUpdate],
    );

    // Gasto / Ingreso: el signo del importe. Con importe 0 solo cambia el
    // signo con el que se empezará a teclear.
    const [esIngreso, setEsIngreso] = useState(transaction.amount > 0);
    useEffect(() => {
      if (transaction.amount !== 0) {
        setEsIngreso(transaction.amount > 0);
      }
    }, [transaction.amount]);
    const onCambiarSigno = useCallback(
      (ingreso: boolean) => {
        setEsIngreso(ingreso);
        const conSigno = ingreso
          ? Math.abs(transaction.amount)
          : -Math.abs(transaction.amount);
        if (conSigno !== transaction.amount) {
          void onUpdateInner(transaction, 'amount', conSigno);
        }
      },
      [onUpdateInner, transaction],
    );
    const { huecoDe } = useColoresCategorias();

    const onTotalAmountUpdate = useCallback(
      (value: number) => {
        if (transaction.amount !== value) {
          void onUpdateInner(transaction, 'amount', value);
        }
      },
      [onUpdateInner, transaction],
    );

    const onEditFieldInner = useCallback(
      (
        transactionId: TransactionEntity['id'],
        name: 'category' | 'payee' | 'account' | 'date' | 'amount' | 'notes',
      ) => {
        onRequestActiveEdit?.(getFieldName(transaction.id, name), () => {
          const transactionToEdit = transactions.find(
            t => t.id === transactionId,
          );
          const unserializedTransaction = unserializedTransactions.find(
            t => t.id === transactionId,
          );

          if (!unserializedTransaction || !transactionToEdit) {
            throw new Error(`Transaction ${transactionId} not found`);
          }
          // Las partes de un movimiento dividido heredan el beneficiario.
          const payeeDelMovimiento =
            getPayee(unserializedTransaction) ??
            getPayee(unserializedTransactions[0]);

          switch (name) {
            case 'category':
              dispatch(
                pushModal({
                  modal: {
                    name: 'category-autocomplete',
                    options: {
                      categoryGroups,
                      showNoneOption: true,
                      month: monthUtils.monthFromDate(
                        unserializedTransaction.date,
                      ),
                      contexto: {
                        beneficiarioId: payeeDelMovimiento?.id ?? null,
                        beneficiario: payeeDelMovimiento?.name,
                        importe: unserializedTransaction.amount,
                        fecha: unserializedTransaction.date,
                        movimientoId: unserializedTransaction.id,
                      },
                      permitirCrear: true,
                      listoParaAsignar: true,
                      onSelect: categoryId => {
                        void onUpdateInner(
                          transactionToEdit,
                          name,
                          categoryId as TransactionEntity['category'],
                        );
                      },
                      onClose: () => {
                        onClearActiveEdit();
                      },
                    },
                  },
                }),
              );
              break;
            case 'account':
              dispatch(
                pushModal({
                  modal: {
                    name: 'account-autocomplete',
                    options: {
                      onSelect: accountId => {
                        void onUpdateInner(transactionToEdit, name, accountId);
                      },
                      onClose: () => {
                        onClearActiveEdit();
                      },
                    },
                  },
                }),
              );
              break;
            case 'payee':
              dispatch(
                pushModal({
                  modal: {
                    name: 'payee-autocomplete',
                    options: {
                      mostrarTraspasos: !unserializedTransaction.is_child,
                      cuentaMovimiento: unserializedTransaction.account,
                      onSelect: payeeId => {
                        void onUpdateInner(transactionToEdit, name, payeeId);
                      },
                      onClose: () => {
                        onClearActiveEdit();
                      },
                    },
                  },
                }),
              );
              break;
            default:
              dispatch(
                pushModal({
                  modal: {
                    name: 'edit-field',
                    options: {
                      name,
                      onSubmit: (name, value) => {
                        if (typeof value === 'object' && 'useRegex' in value) {
                          void onUpdateInner(
                            transactionToEdit,
                            name,
                            applyFindReplace(
                              transactionToEdit.notes,
                              value.find,
                              value.replace,
                              value.useRegex,
                            ),
                          );
                        } else {
                          void onUpdateInner(transactionToEdit, name, value);
                        }
                      },
                      onClose: () => {
                        onClearActiveEdit();
                      },
                    },
                  },
                }),
              );
              break;
          }
        });
      },
      [
        categoryGroups,
        dispatch,
        getPayee,
        onUpdateInner,
        onClearActiveEdit,
        onRequestActiveEdit,
        transaction.id,
        transactions,
        unserializedTransactions,
      ],
    );

    const onDeleteInner = useCallback(
      async (id: TransactionEntity['id']) => {
        const [parentTransaction] = unserializedTransactions;
        const targetTransaction =
          unserializedTransactions.find(t => t.id === id) ?? parentTransaction;

        const onConfirmDelete = () => {
          dispatch(
            pushModal({
              modal: {
                name: 'confirm-delete',
                options: {
                  message: t(
                    'Are you sure you want to delete the transaction?',
                  ),
                  // B6 · H73: comercio e importe en la hoja de confirmar.
                  contexto: {
                    comercio: getPayee(targetTransaction)?.name ?? null,
                    importe: targetTransaction.amount ?? null,
                  },
                  onConfirm: () => {
                    onDelete(id);

                    if (parentTransaction.id !== id) {
                      // Only a child transaction was deleted.
                      onClearActiveEdit();
                      return;
                    }

                    salir(() => void navigate(-1));
                  },
                },
              },
            }),
          );
        };

        if (targetTransaction.reconciled) {
          dispatch(
            pushModal({
              modal: {
                name: 'confirm-transaction-edit',
                options: {
                  onConfirm: onConfirmDelete,
                  confirmReason: 'deleteReconciled',
                },
              },
            }),
          );
        } else if (targetTransaction.transfer_id) {
          const { data } = await aqlQuery(
            q('transactions')
              .filter({
                id: targetTransaction.transfer_id,
                reconciled: true,
              })
              .select('id'),
          );
          if ((data as TransactionEntity[]).length > 0) {
            dispatch(
              pushModal({
                modal: {
                  name: 'confirm-transaction-edit',
                  options: {
                    onConfirm: onConfirmDelete,
                    confirmReason: 'batchDeleteWithReconciledTransfer',
                  },
                },
              }),
            );
          } else {
            onConfirmDelete();
          }
        } else {
          onConfirmDelete();
        }
      },
      [
        dispatch,
        getPayee,
        navigate,
        onClearActiveEdit,
        onDelete,
        unserializedTransactions,
        t,
        salir,
      ],
    );

    const scrollChildTransactionIntoView = useCallback(
      (id: TransactionEntity['id']) => {
        const childTransactionEditElement =
          childTransactionElementRefMap.current?.[id];
        childTransactionEditElement?.scrollIntoView({
          behavior: 'smooth',
        });
      },
      [],
    );

    const onEmptySplitFound = useCallback(
      (id: TransactionEntity['id']) => {
        scrollChildTransactionIntoView(id);
      },
      [scrollChildTransactionIntoView],
    );

    const account = getAccount(transaction);
    const isOffBudget = account ? !!account.offbudget : false;
    const transferAccount = getTransferAccount(transaction);
    const esTraspaso = !!transferAccount && !transaction.is_parent;
    const title = getPrettyPayee({
      t,
      transaction,
      payee: getPayee(transaction),
      transferAccount,
    });

    // Traspaso: el signo dice el sentido. Negativo = sale de esta cuenta.
    const cuentaDesde = esIngreso ? transferAccount : account;
    const cuentaHacia = esIngreso ? account : transferAccount;
    // De una cuenta fuera de presupuesto a una del presupuesto: el dinero
    // entra en «Listo para asignar» (categoría de ingreso en Actual).
    const traspasoAlPresupuesto =
      esTraspaso && !!cuentaDesde?.offbudget && !cuentaHacia?.offbudget;
    const categoriaActual = categories.find(c => c.id === transaction.category);
    // Categoría de ingreso para «Listo para asignar»: la que se llame así (o
    // «Income»/«Ingresos»); si no, la primera visible que no sea la de saldos
    // iniciales.
    const categoriaIngreso = useMemo(
      () => elegirCategoriaIngreso(categories),
      [categories],
    );
    useEffect(() => {
      if (
        traspasoAlPresupuesto &&
        !isOffBudget &&
        !transaction.category &&
        categoriaIngreso
      ) {
        void onUpdateInner(transaction, 'category', categoriaIngreso.id);
      }
    }, [
      traspasoAlPresupuesto,
      isOffBudget,
      transaction,
      categoriaIngreso,
      onUpdateInner,
    ]);

    const [codigoMoneda] = useSyncedPref('defaultCurrencyCode');
    const simbolo = getCurrency(codigoMoneda || '').symbol || '€';

    // «No contar como ingreso»: etiqueta `#noingreso` en la nota (ver
    // `informes/noIngreso.ts`). Los traspasos nunca cuentan.
    const noIngreso = esTraspaso || tieneEtiquetaNoIngreso(transaction.notes);
    const [versionNota, setVersionNota] = useState(0);
    const verNoIngreso =
      !transaction.is_parent &&
      !isOffBudget &&
      (esTraspaso ? traspasoAlPresupuesto : esIngreso);

    const tipo: TipoMovimiento = esTraspaso
      ? 'traspaso'
      : esIngreso
        ? 'ingreso'
        : 'gasto';

    const abrirTraspaso = useCallback(() => {
      onRequestActiveEdit?.(getFieldName(transaction.id, 'payee'), () => {
        dispatch(
          pushModal({
            modal: {
              name: 'payee-autocomplete',
              options: {
                mostrarTraspasos: true,
                empezarEnTraspaso: true,
                cuentaMovimiento: transaction.account,
                onSelect: payeeId => {
                  void onUpdateInner(transaction, 'payee', payeeId);
                },
                onClose: () => {
                  onClearActiveEdit();
                },
              },
            },
          }),
        );
      });
    }, [
      dispatch,
      onClearActiveEdit,
      onRequestActiveEdit,
      onUpdateInner,
      transaction,
    ]);

    const onCambiarTipo = useCallback(
      (nuevo: TipoMovimiento) => {
        if (nuevo === 'traspaso') {
          if (!esTraspaso) {
            abrirTraspaso();
          }
          return;
        }
        if (esTraspaso) {
          // Deja de ser traspaso: sin beneficiario de cuenta.
          void onUpdateInner(transaction, 'payee', null);
        }
        onCambiarSigno(nuevo === 'ingreso');
      },
      [abrirTraspaso, esTraspaso, onCambiarSigno, onUpdateInner, transaction],
    );

    const transactionDate = parseDate(transaction.date, dateFormat, new Date());
    const dateDefaultValue = monthUtils.dayFromDate(transactionDate);

    const remaining = transaction.error?.difference ?? 0;

    const categoriaNodo = (() => {
      if (isOffBudget || isBudgetTransfer(transaction)) {
        return undefined;
      }
      if (esTraspaso && categoriaActual?.is_income) {
        return (
          <Pildora estado="ok" data-testid="listo-para-asignar">
            {t('Ready to Assign')}
          </Pildora>
        );
      }
      return (
        <PildoraCategoria
          nombre={getCategory(transaction, isOffBudget) ?? ''}
          hueco={transaction.category ? huecoDe(transaction.category) : null}
        />
      );
    })();
    // Entre dos cuentas del presupuesto (o desde una cuenta fuera de él) el
    // traspaso no lleva categoría: no se pinta la fila.
    const verCategoria =
      !transaction.is_parent &&
      !(esTraspaso && (isOffBudget || isBudgetTransfer(transaction)));

    // «Por revisar»: ¿se puede aprobar tal cual? (regla en `puedeAprobarse`).
    // Sin la categoría sugerida: en el editor solo se aprueba con una
    // categoría elegida a propósito (decisión del 6-oct-2026); en Revisar,
    // tocar el chip «¿Supermercado? ✓» sí es elegirla.
    const aprobable = (() => {
      const [padre, ...hijos] = unserializedTransactions;
      if (!padre) {
        return false;
      }
      return puedeAprobarse(
        {
          category: padre.category ?? null,
          account: padre.account,
          is_parent: padre.is_parent,
          transfer_id: padre.transfer_id,
          partes: hijos.map(h => ({
            category: h.category ?? null,
            transfer_id: h.transfer_id,
          })),
        },
        new Set(isOffBudget ? [padre.account] : []),
      );
    })();

    // H69/H79: un único botón principal («Añadir», «Guardar» o «Guardar y
    // aprobar») con el motivo encima cuando aún no se puede pulsar.
    const porRevisar =
      !!revisar && unserializedTransactions[0]?.reviewed === false;
    const estado = estadoPie({
      isAdding,
      account: transaction.account,
      amount: unserializedTransactions[0]?.amount ?? 0,
      porRevisar,
      aprobable,
    });

    const [ayudaAbierta, setAyudaAbierta] = useState(false);
    const preguntasAyuda = usePreguntasDetalleMovimiento({
      anadir: isAdding,
      dividido: childTransactions.length > 0,
      fueraPresupuesto: !!isOffBudget,
      cuadrado: !!transaction.reconciled,
      estado,
    });

    const onGuardarYAprobar = async () => {
      if (!revisar) {
        return;
      }
      const [padre, ...hijos] = unserializedTransactions;
      registrarUso('movimiento', { accion: 'editar', n: 1 + hijos.length });
      await onSave([{ ...padre, reviewed: true }, ...hijos]);
      revisar.onAprobado();
    };

    return (
      <Page
        header={
          <MobilePageHeader
            title={
              <span data-ayuda="titulo-detalle">
                {esTraspaso
                  ? t('Transfer (type)')
                  : transaction.payee == null
                    ? isAdding
                      ? t('New Transaction')
                      : t('Transaction')
                    : title}
              </span>
            }
            leftContent={<MobileBackButton />}
            rightContent={<OjoBuey onPress={() => setAyudaAbierta(true)} />}
          />
        }
        footer={
          <Footer
            transactions={transactions}
            estado={estado}
            onPrincipal={
              estado.accion === 'guardarYAprobar'
                ? onGuardarYAprobar
                : onSaveInner
            }
            onSplit={onSplit}
            onAddSplit={onAddSplit}
            onEmptySplitFound={onEmptySplitFound}
            editingField={editingField}
          />
        }
        padding={0}
      >
        <View
          data-testid="transaction-form"
          style={{ flexShrink: 0, marginTop: 8, marginBottom: 16, gap: 12 }}
        >
          <View
            data-testid="importe-movimiento"
            data-ingreso={esIngreso || undefined}
            data-tipo={tipo}
            style={{
              alignItems: 'center',
              gap: 10,
              margin: '0 16px',
              padding: '12px 12px 12px',
              borderRadius: 16,
              // H05: el campo del importe se delimita con el color del tipo.
              border: `1.5px solid ${TINTE_TIPO[tipo]}`,
              boxShadow: sombra.tarjeta,
              backgroundColor: color.surface,
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                maxWidth: '100%',
              }}
            >
              <AmountInput
                value={transaction.amount}
                disabled={
                  !!editingField &&
                  editingField !== getFieldName(transaction.id, 'amount')
                }
                autoFocus={isAdding}
                disableNativeAutoFocusOnIOS
                onFocus={() => {
                  onRequestActiveEdit(getFieldName(transaction.id, 'amount'));
                }}
                onBlur={() => onClearActiveEdit()}
                onChange={onTotalAmountUpdate}
                negate={!esIngreso}
                variant="large"
                tamanoGrande={30}
              />
              <Text
                aria-hidden
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: color.fg3,
                  userSelect: 'none',
                }}
              >
                {simbolo}
              </Text>
            </View>
            <SelectorTipo
              tipo={tipo}
              isDisabled={!!editingField || !!transaction.is_parent}
              onChange={onCambiarTipo}
            />
          </View>

          {esTraspaso ? (
            <View
              data-testid="traspaso"
              style={{ ...tarjetaFormularioStyle, position: 'relative' }}
            >
              <FilaCuentaTraspaso
                etiqueta={t('From (transfer)')}
                cuenta={cuentaDesde}
                testId="traspaso-desde"
                isDisabled={!!editingField}
                onPress={() =>
                  esIngreso
                    ? abrirTraspaso()
                    : onEditFieldInner(transaction.id, 'account')
                }
              />
              <FilaCuentaTraspaso
                etiqueta={t('To (transfer)')}
                cuenta={cuentaHacia}
                testId="traspaso-hacia"
                isDisabled={!!editingField}
                onPress={() =>
                  esIngreso
                    ? onEditFieldInner(transaction.id, 'account')
                    : abrirTraspaso()
                }
              />
              <Button
                variant="bare"
                aria-label={t('Swap direction')}
                data-testid="traspaso-invertir"
                isDisabled={!!editingField}
                onPress={() => onCambiarSigno(!esIngreso)}
                style={{
                  position: 'absolute',
                  // Sobre la línea entre «Desde» y «Hacia», tras la etiqueta;
                  // zona táctil de 44 px con un círculo de 28 dentro.
                  left: 14 + 28 + 12 + 40,
                  top: 48 - 22,
                  width: 44,
                  height: 44,
                  minHeight: 44,
                  padding: 0,
                  border: 0,
                  backgroundColor: 'transparent',
                  zIndex: 1,
                }}
              >
                <View
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 999,
                    border: `1px solid ${color.line2}`,
                    backgroundColor: color.surface,
                    color: color.accent,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icono nombre="arrowdown" size={15} />
                </View>
              </Button>
              {verCategoria && (
                <TapField
                  etiqueta={t('Category')}
                  valorNodo={categoriaNodo}
                  icon={<SvgTag width={15} height={15} />}
                  placeholder={t('Select a category')}
                  rightContent={dropdownChevron}
                  value={getCategory(transaction, isOffBudget)}
                  isDisabled={
                    !!bloqueaSelectores &&
                    bloqueaSelectores !==
                      getFieldName(transaction.id, 'category')
                  }
                  onPress={() => onEditFieldInner(transaction.id, 'category')}
                  data-testid="category-field"
                />
              )}
            </View>
          ) : (
            <View style={tarjetaFormularioStyle}>
              <TapField
                etiqueta={t('Payee')}
                icon={<SvgUser width={15} height={15} />}
                placeholder={
                  transaction.amount > 0
                    ? t('Who paid you?')
                    : t('Who did you pay?')
                }
                textStyle={{
                  ...(transaction.is_parent && {
                    fontStyle: 'italic',
                    fontWeight: 300,
                  }),
                }}
                value={title}
                isDisabled={
                  !!bloqueaSelectores &&
                  bloqueaSelectores !== getFieldName(transaction.id, 'payee')
                }
                onPress={() => onEditFieldInner(transaction.id, 'payee')}
                data-testid="payee-field"
                alwaysShowRightContent={
                  (!!nearestPayee || !!onRequestLocation) &&
                  !transaction.payee &&
                  !shouldShowSaveLocation
                }
                rightContent={
                  shouldShowSaveLocation ? (
                    <Button
                      variant="bare"
                      onPress={onSaveLocation}
                      style={botonUbicacion}
                    >
                      <Trans>Save</Trans>
                      <SvgLocation
                        width={10}
                        height={10}
                        style={{ marginLeft: 4 }}
                      />
                    </Button>
                  ) : nearestPayee && !transaction.payee ? (
                    <Button
                      variant="bare"
                      onPress={onSelectNearestPayee}
                      style={botonUbicacion}
                    >
                      <Trans>Nearby</Trans>
                      <SvgLocation
                        width={10}
                        height={10}
                        style={{ marginLeft: 4 }}
                      />
                    </Button>
                  ) : onRequestLocation && !transaction.payee ? (
                    <Button
                      variant="bare"
                      onPress={onRequestLocation}
                      style={botonUbicacion}
                    >
                      <Trans>Request Location</Trans>
                      <SvgLocation
                        width={10}
                        height={10}
                        style={{ marginLeft: 4 }}
                      />
                    </Button>
                  ) : (
                    dropdownChevron
                  )
                }
              />

              {verCategoria && (
                <TapField
                  etiqueta={t('Category')}
                  valorNodo={categoriaNodo}
                  icon={<SvgTag width={15} height={15} />}
                  placeholder={t('Select a category')}
                  rightContent={dropdownChevron}
                  style={{
                    ...(isOffBudget && {
                      fontStyle: 'italic',
                      color: theme.pageTextSubdued,
                      fontWeight: 300,
                    }),
                  }}
                  value={getCategory(transaction, isOffBudget)}
                  isDisabled={
                    (!!bloqueaSelectores &&
                      bloqueaSelectores !==
                        getFieldName(transaction.id, 'category')) ||
                    isOffBudget
                  }
                  onPress={() => onEditFieldInner(transaction.id, 'category')}
                  data-testid="category-field"
                />
              )}

              <TapField
                etiqueta={t('Account')}
                icon={<SvgWallet width={15} height={15} />}
                placeholder={t('Select an account')}
                rightContent={dropdownChevron}
                isDisabled={
                  !!bloqueaSelectores &&
                  bloqueaSelectores !== getFieldName(transaction.id, 'account')
                }
                value={account?.name}
                onPress={() => onEditFieldInner(transaction.id, 'account')}
                data-testid="account-field"
              />
            </View>
          )}

          {childTransactions.map((childTrans, i, arr) => (
            <ChildTransactionEdit
              key={childTrans.id}
              transaction={childTrans}
              negate={transaction.amount <= 0}
              amountFocused={arr.findIndex(c => c.amount === 0) === i}
              ref={r => {
                childTransactionElementRefMap.current = {
                  ...childTransactionElementRefMap.current,
                  [childTrans.id]: r,
                };
              }}
              isOffBudget={isOffBudget}
              getCategory={getCategory}
              getPayee={getPayee}
              getTransferAccount={getTransferAccount}
              isBudgetTransfer={isBudgetTransfer}
              onUpdate={onUpdateInner}
              onEditField={onEditFieldInner}
              onDelete={onDeleteInner}
              keyboardHeader={
                <FillRemainingButton
                  remaining={remaining}
                  onPress={() => {
                    void onUpdateInner(
                      childTrans,
                      'amount',
                      integerToAmount(remaining),
                    );
                  }}
                />
              }
            />
          ))}

          <View style={tarjetaFormularioStyle}>
            <InputField
              etiqueta={t('Date')}
              dataAyuda="fila-fecha"
              type="date"
              iconStart={<SvgCalendar width={15} height={15} />}
              disabled={
                !!bloqueaSelectores &&
                bloqueaSelectores !== getFieldName(transaction.id, 'date')
              }
              required
              style={{
                minWidth: '120px',
                appearance: 'none',
              }}
              defaultValue={dateDefaultValue}
              textoVisible={fechaLegible(dateDefaultValue, locale)}
              onBlur={() => onClearActiveEdit()}
              onFocus={() =>
                onRequestActiveEdit(getFieldName(transaction.id, 'date'))
              }
              onChange={event =>
                onUpdateInner(
                  transaction,
                  'date',
                  formatDate(parseISO(event.target.value), dateFormat),
                )
              }
            />
            <FilaInterruptor
              icono={<SvgCheckCircle1 width={15} height={15} />}
              etiqueta={transaction.reconciled ? t('Reconciled') : t('Cleared')}
              testId="fila-confirmado"
            >
              {transaction.reconciled ? (
                <Toggle id="Reconciled" isOn isDisabled />
              ) : (
                <ToggleField
                  id="cleared"
                  isOn={!!transaction.cleared}
                  onToggle={on => onUpdateInner(transaction, 'cleared', on)}
                />
              )}
            </FilaInterruptor>
            {verNoIngreso && (
              <FilaInterruptor
                icono={<Icono nombre="coins" size={15} />}
                etiqueta={t("Don't count as income")}
                detalle={
                  esTraspaso
                    ? t('Transfers between your accounts never count.')
                    : t('Still adds to Ready to Assign.')
                }
                testId="no-contar-ingreso"
              >
                <ToggleField
                  id="no-ingreso"
                  isOn={noIngreso}
                  isDisabled={esTraspaso || !!editingField}
                  onToggle={on => {
                    void onUpdateInner(
                      transaction,
                      'notes',
                      conEtiquetaNoIngreso(transaction.notes, on),
                    );
                    setVersionNota(v => v + 1);
                  }}
                />
              </FilaInterruptor>
            )}

            <InputField
              key={versionNota}
              etiqueta={t('Notes')}
              dataAyuda="fila-notas"
              ref={noteRef}
              iconStart={<SvgNotesPaper width={15} height={15} />}
              iconEnd={<NoteInsertHashButton inputRef={noteRef} />}
              placeholder={t('Add a note (optional)')}
              disabled={
                !!bloqueaSelectores &&
                bloqueaSelectores !== getFieldName(transaction.id, 'notes')
              }
              defaultValue={transaction.notes}
              onFocus={() => {
                onRequestActiveEdit(getFieldName(transaction.id, 'notes'));
              }}
              onBlur={() => onClearActiveEdit()}
              onChange={event =>
                onUpdateInner(transaction, 'notes', event.target.value)
              }
            />
          </View>

          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            {transaction.amount !== 0 &&
              childTransactions.length === 0 &&
              !esTraspaso && (
                <Button
                  variant="bare"
                  data-ayuda="dividir"
                  isDisabled={!!editingField}
                  style={botonSecundario}
                  onPress={() => onSplit(transaction.id)}
                >
                  <SvgSplit width={15} height={15} />
                  <Text style={{ marginLeft: 6, userSelect: 'none' }}>
                    <Trans>Split</Trans>
                  </Text>
                </Button>
              )}
            {!isAdding && (
              <Button
                variant="bare"
                data-ayuda="eliminar-movimiento"
                onPress={() => onDeleteInner(transaction.id)}
                style={{ ...botonSecundario, color: color.bad }}
              >
                <SvgTrash width={15} height={15} />
                <Text style={{ marginLeft: 6, userSelect: 'none' }}>
                  <Trans>Delete transaction</Trans>
                </Text>
              </Button>
            )}
          </View>
          <NoteTagAutocomplete inputRef={noteRef} />
        </View>
        {ayudaAbierta && (
          <AyudaGavi
            pantalla={t('Transaction')}
            preguntas={preguntasAyuda}
            onCerrar={() => setAyudaAbierta(false)}
          />
        )}
      </Page>
    );
  },
);

function isTemporary(transaction: TransactionEntity) {
  return transaction.id.indexOf('temp') === 0;
}

type TransactionEditUnconnectedProps = {
  categories: CategoryEntity[];
  accounts: AccountEntity[];
  payees: PayeeEntity[];
  lastTransaction: TransactionEntity | null;
  lastTransactionAt: number | null;
  dateFormat: string;
};

function TransactionEditUnconnected({
  categories,
  accounts,
  payees,
  lastTransaction,
  lastTransactionAt,
  dateFormat,
}: TransactionEditUnconnectedProps) {
  const { t } = useTranslation();
  const { transactionId } = useParams();
  const { state: locationState } = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const updatePayeeLocationMutation = useSavePayeeLocationMutation();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<TransactionEntity[]>([]);
  const [fetchedTransactions, setFetchedTransactions] = useState<
    TransactionEntity[]
  >([]);
  const isAdding = useRef(false);
  // Lo que había al abrir el nuevo movimiento: para saber si hay algo escrito.
  const [inicial, setInicial] = useState<TransactionEntity[]>([]);
  const [aprenderPref = 'true'] = useSyncedPref('learn-categories');
  const aprender = String(aprenderPref) === 'true';
  const isDeleted = useRef(false);

  // Algo escrito sin guardar: «atrás» pregunta antes de tirarlo.
  const sucio =
    transactions.length > 0 &&
    JSON.stringify(transactions) !==
      JSON.stringify(transactionId === 'new' ? inicial : fetchedTransactions);
  const { aviso: avisoSalida, salir } = useAvisoSalida(sucio);

  // «Por revisar»: llegado desde /revisar, «Aprobar» guarda, aprueba y
  // vuelve siempre a la lista, aunque queden más (Vadik, 12-oct-2026).
  const enRevision = searchParams.get('revisar') != null;
  const revisar = useMemo(() => {
    if (!enRevision || !transactionId) {
      return null;
    }
    return {
      onAprobado: () => {
        salir(() => void navigate(-1));
      },
    };
  }, [enRevision, transactionId, navigate, salir]);

  const searchParamCategory = useMemo(
    () => categories.find(c => c.name === searchParams.get('category'))?.id,
    [categories, searchParams],
  );
  const searchParamAccount = useMemo(
    () => accounts.find(a => a.name === searchParams.get('account'))?.id,
    [accounts, searchParams],
  );
  // H67: cuenta de un movimiento nuevo; si no viene de una cuenta, la
  // última usada (recordada en este dispositivo) mientras siga abierta.
  // Es un id (texto): el efecto de abajo no se repite si solo cambia la
  // lista de cuentas.
  const cuentaInicial = useMemo(
    () =>
      cuentaPorDefecto({
        deUrl: searchParamAccount,
        deNavegacion: locationState?.accountId,
        delUltimo: lastTransaction?.account,
        recordada: leerUltimaCuenta(),
        abiertas: accounts.filter(a => !a.closed),
      }),
    [accounts, searchParamAccount, locationState?.accountId, lastTransaction],
  );
  const searchParamPayee = useMemo(
    () => payees.find(p => p.name === searchParams.get('payee'))?.id,
    [payees, searchParams],
  );

  const {
    isGranted: isLocationGranted,
    isPending: shouldPromptLocation,
    requestPermission,
  } = useLocationPermission();
  const { data: nearbyPayees = [] } = useNearbyPayees({
    enabled: isLocationGranted,
  });
  const [shouldShowSaveLocation, setShouldShowSaveLocation] = useState(false);
  const nearestPayee = nearbyPayees[0]?.payee ?? null;

  useEffect(() => {
    let unmounted = false;

    async function fetchTransaction() {
      // Query for the transaction based on the ID with grouped splits.
      //
      // This means if the transaction in question is a split transaction, its
      // subtransactions will be returned in the `substransactions` property on
      // the parent transaction.
      //
      // The edit item components expect to work with a flat array of
      // transactions when handling splits, so we call ungroupTransactions to
      // flatten parent and children into one array.
      const { data } = await aqlQuery(
        q('transactions')
          .filter({ id: transactionId })
          .select('*')
          .options({ splits: 'grouped' }),
      );

      if (!unmounted) {
        const fetchedTransactions = ungroupTransactions(data);
        setTransactions(fetchedTransactions);
        setFetchedTransactions(fetchedTransactions);
      }
    }
    // H69: el editor puede seguir montado al pasar de «nuevo» a uno que ya
    // existe (Revisar → siguiente): sin esto diría «Añadir» al editar.
    isAdding.current = transactionId === 'new';
    if (transactionId !== 'new') {
      void fetchTransaction();
    } else {
      isAdding.current = true;
    }

    return () => {
      unmounted = true;
    };
  }, [transactionId]);

  useEffect(() => {
    if (!isLocationGranted) {
      setShouldShowSaveLocation(false);
    }
  }, [isLocationGranted]);

  useEffect(() => {
    if (isAdding.current) {
      const nuevos: TransactionEntity[] = [
        {
          id: 'temp',
          date: (() => {
            const dateParam = searchParams.get('date') || '';
            if (!isNaN(Date.parse(dateParam))) {
              return dateParam;
            }
            return fechaPorDefectoNuevoMovimiento({
              fechaUltimo: lastTransaction?.date,
              apuntadoEn: lastTransactionAt,
              ahora: Date.now(),
              hoy: monthUtils.currentDay(),
            });
          })(),
          payee: searchParamPayee,
          account: cuentaInicial ?? '',
          category: searchParamCategory || locationState?.categoryId || null,
          amount: -amountToInteger(
            parseFloat(searchParams.get('amount') || '') || 0,
          ),
          cleared: searchParams.get('cleared') === 'true',
          notes: searchParams.get('notes') || '',
        },
      ];
      setTransactions(nuevos);
      setInicial(nuevos);
    }
  }, [
    cuentaInicial,
    locationState?.categoryId,
    lastTransaction,
    lastTransactionAt,
    searchParamCategory,
    searchParamPayee,
    searchParams,
  ]);

  const onUpdate = useCallback(
    async (
      serializedTransaction: TransactionEntity,
      updatedField: keyof TransactionEntity,
    ) => {
      const transaction = deserializeTransaction(
        serializedTransaction,
        null,
        dateFormat,
      );

      // Run the rules to auto-fill in any data. Right now we only do
      // this on new transactions because that's how desktop works.
      const newTransaction = { ...transaction };
      const changedFields = new Set<keyof TransactionEntity>([updatedField]);
      if (isTemporary(newTransaction)) {
        const afterRules = await send('rules-run', {
          transaction: newTransaction,
        });
        const diff = getChangedValues(newTransaction, afterRules);

        if (diff) {
          Object.keys(diff).forEach(key => {
            const field = key as keyof TransactionEntity;
            // Apply rule changes to "empty" fields and append/prepend notes rules
            // (see shouldApplyRuleChange).
            // Or update all fields if the payee changes (assists location-based entry by
            // applying rules to prefill category, notes, etc. based on the selected payee)
            if (
              updatedField === 'payee' ||
              shouldApplyRuleChange(field, newTransaction[field], diff[field])
            ) {
              (newTransaction as Record<string, unknown>)[field] = diff[field];
              changedFields.add(field);
            }
          });

          // When a rule updates a parent transaction, overwrite all changes to the current field in subtransactions.
          if (
            newTransaction.is_parent &&
            diff.subtransactions !== undefined &&
            updatedField !== null
          ) {
            newTransaction.subtransactions = diff.subtransactions.map(
              (st, idx) => ({
                ...(newTransaction.subtransactions?.[idx] || st),
                ...(st[updatedField] != null && {
                  [updatedField]: st[updatedField],
                }),
              }),
            );
            changedFields.add('subtransactions');
          }
        }

        // H67: al elegir beneficiario en un gasto nuevo sin categoría (ni
        // regla que la ponga), su categoría habitual: la de su último
        // movimiento categorizado.
        if (
          updatedField === 'payee' &&
          newTransaction.payee &&
          !newTransaction.category &&
          !newTransaction.is_parent &&
          !payees.find(p => p.id === newTransaction.payee)?.transfer_acct
        ) {
          const habitual = await categoriaHabitual(newTransaction.payee);
          if (habitual) {
            newTransaction.category = habitual;
            changedFields.add('category');
          }
        }
      }

      // Updates can be in flight at the same time (e.g. an amount blur
      // racing a nearby payee press), so merge only the changed fields onto
      // the latest state rather than replacing the whole transaction.
      setTransactions(prevTransactions => {
        const latestTransaction = prevTransactions.find(
          t => t.id === newTransaction.id,
        );
        if (!latestTransaction) {
          // The transaction was deleted while this update was in flight.
          return prevTransactions;
        }
        const merged = { ...latestTransaction };
        for (const field of changedFields) {
          (merged as Record<string, unknown>)[field] = newTransaction[field];
        }
        return updateTransaction(prevTransactions, merged).data;
      });

      if (updatedField === 'payee') {
        setShouldShowSaveLocation(false);

        if (newTransaction.payee && isLocationGranted) {
          const payeeLocations = await locationService.getPayeeLocations(
            newTransaction.payee,
          );
          if (payeeLocations.length === 0) {
            setShouldShowSaveLocation(true);
          } else {
            const currentPosition = await locationService.getCurrentPosition();
            const hasNearby = payeeLocations.some(
              loc =>
                calculateDistance(currentPosition, {
                  latitude: loc.latitude,
                  longitude: loc.longitude,
                }) <= DEFAULT_MAX_DISTANCE_METERS,
            );
            if (!hasNearby) {
              setShouldShowSaveLocation(true);
            }
          }
        }
      }
    },
    [dateFormat, isLocationGranted, payees],
  );

  const onSave = useCallback(
    async (newTransactions: TransactionEntity[]) => {
      if (isDeleted.current) {
        return;
      }
      // Puerta de planes: en «solo ver» no se guarda; se abre la hoja.
      if (!comprobarPlan()) {
        return;
      }

      const changes = diffItems(fetchedTransactions || [], newTransactions);
      if (
        changes.added.length > 0 ||
        changes.updated.length > 0 ||
        changes.deleted.length
      ) {
        const _remoteUpdates = await send('transactions-batch-update', {
          added: changes.added,
          deleted: changes.deleted,
          updated: changes.updated,
          // Rumbo: la categoría que eliges enseña a la regla del comercio.
          learnCategories: aprender,
        });

        // if (onTransactionsChange) {
        //   onTransactionsChange({
        //     ...changes,
        //     updated: changes.updated.concat(remoteUpdates),
        //   });
        // }
      }

      if (isAdding.current) {
        // The first one is always the "parent" and the only one we care
        // about
        dispatch(
          setLastTransaction({
            transaction: newTransactions[0],
            at: Date.now(),
          }),
        );
      }
    },
    [dispatch, fetchedTransactions, aprender],
  );

  const onDelete = useCallback(
    async (id: TransactionEntity['id']) => {
      const changes = deleteTransaction(transactions, id);

      if (isAdding.current) {
        // Adding a new transactions, this disables saving when the component unmounts
        isDeleted.current = true;
      } else {
        const _remoteUpdates = await send('transactions-batch-update', {
          deleted: changes.diff.deleted,
        });

        // if (onTransactionsChange) {
        //   onTransactionsChange({ ...changes, updated: remoteUpdates });
        // }
      }

      setTransactions(changes.data);
    },
    [transactions],
  );

  const onAddSplit = useCallback(
    (id: TransactionEntity['id']) => {
      const changes = addSplitTransaction(transactions, id);
      setTransactions(changes.data);
    },
    [transactions],
  );

  const onSplit = useCallback(
    (id: TransactionEntity['id']) => {
      const changes = splitTransaction(
        transactions,
        id,
        makeEmptySplitSubtransactions,
      );

      setTransactions(changes.data);
    },
    [transactions],
  );

  const onSaveLocation = useCallback(async () => {
    try {
      const [transaction] = transactions;
      if (transaction.payee) {
        await updatePayeeLocationMutation.mutateAsync(transaction.payee);
        setShouldShowSaveLocation(false);
      }
    } catch (error) {
      console.error('Failed to save location', { error });
      dispatch(
        addNotification({
          notification: {
            type: 'error',
            message: t('Failed to save location'),
          },
        }),
      );
    }
  }, [t, transactions, dispatch, updatePayeeLocationMutation]);

  const onSelectNearestPayee = useCallback(() => {
    const transaction = transactions[0];
    if (!nearestPayee || !transaction || transaction.payee) {
      return;
    }

    const updated = {
      ...serializeTransaction(transaction, dateFormat),
      payee: nearestPayee.id,
    };
    void onUpdate(updated, 'payee');
  }, [transactions, nearestPayee, onUpdate, dateFormat]);

  if (accounts.length === 0) {
    return (
      <Page
        header={
          <MobilePageHeader
            title={t('New Transaction')}
            leftContent={<MobileBackButton />}
          />
        }
        padding={0}
      >
        <View
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            backgroundColor: theme.mobilePageBackground,
          }}
        >
          <Text
            style={{
              fontSize: 15,
              textAlign: 'center',
              marginBottom: 20,
              lineHeight: '1.5em',
            }}
          >
            <Trans>
              To add a transaction, you need to{' '}
              <strong>create an account first</strong>. You can add an account
              from the accounts page.
            </Trans>
          </Text>
          <Button
            variant="primary"
            onPress={() => {
              dispatch(
                pushModal({
                  modal: { name: 'add-account', options: {} },
                }),
              );
            }}
          >
            <Trans>Add account</Trans>
          </Button>
        </View>
      </Page>
    );
  }

  if (categories.length === 0) {
    return (
      <Page
        header={
          <MobilePageHeader
            title={t('New Transaction')}
            leftContent={<MobileBackButton />}
          />
        }
        padding={0}
      >
        <View
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            backgroundColor: theme.mobilePageBackground,
          }}
        >
          <Text
            style={{
              fontSize: 15,
              textAlign: 'center',
              marginBottom: 20,
              lineHeight: '1.5em',
            }}
          >
            <Trans>
              To add a transaction, you need to{' '}
              <strong>create a category first</strong>. You can add categories
              from the budget page.
            </Trans>
          </Text>
          <Button
            variant="primary"
            onPress={() => {
              void navigate('/budget');
            }}
          >
            <Trans>Go to budget</Trans>
          </Button>
        </View>
      </Page>
    );
  }

  // This check ensures the component only renders after the transaction state
  // has been properly initialized. When creating a new transaction (transactionId === 'new'),
  // the transaction is created in a useEffect that runs after the component mounts.
  // Returning null here acts as a loading state until that initialization completes.
  if (transactions.length === 0) {
    return null;
  }

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: theme.pageBackground,
      }}
    >
      <TransactionEditInner
        transactions={transactions}
        isAdding={transactionId === 'new'}
        categories={categories}
        accounts={accounts}
        payees={payees}
        dateFormat={dateFormat}
        onUpdate={onUpdate}
        onSave={onSave}
        onDelete={onDelete}
        onSplit={onSplit}
        onAddSplit={onAddSplit}
        shouldShowSaveLocation={shouldShowSaveLocation}
        onSaveLocation={onSaveLocation}
        onSelectNearestPayee={onSelectNearestPayee}
        nearestPayee={isLocationGranted ? nearestPayee : null}
        onRequestLocation={shouldPromptLocation ? requestPermission : undefined}
        revisar={revisar}
        salir={salir}
      />
      {avisoSalida}
    </View>
  );
}

type TransactionEditProps = Omit<
  TransactionEditUnconnectedProps,
  | 'categories'
  | 'accounts'
  | 'payees'
  | 'lastTransaction'
  | 'lastTransactionAt'
  | 'dateFormat'
>;

export const TransactionEdit = (props: TransactionEditProps) => {
  const { data: { list: categories } = { list: [] } } = useCategories();
  const { data: payees = [] } = usePayees();
  const lastTransaction = useSelector(
    state => state.transactions.lastTransaction,
  );
  const lastTransactionAt = useSelector(
    state => state.transactions.lastTransactionAt,
  );
  const { data: accounts = [] } = useAccounts();
  const dateFormat = useDateFormat() || 'MM/dd/yyyy';

  return (
    <SingleActiveEditFormProvider formName="mobile-transaction">
      <TransactionEditUnconnected
        {...props}
        categories={categories}
        payees={payees}
        lastTransaction={lastTransaction}
        lastTransactionAt={lastTransactionAt}
        accounts={accounts}
        dateFormat={dateFormat}
      />
    </SingleActiveEditFormProvider>
  );
};

function FillRemainingButton({
  remaining,
  onPress,
}: {
  readonly remaining: number;
  readonly onPress: () => void;
}) {
  const format = useFormat();
  return (
    <Button
      variant="primary"
      style={{ height: styles.mobileMinHeight }}
      onPress={onPress}
      isDisabled={remaining === 0}
    >
      <SvgSplit width={17} height={17} />
      <Text
        style={{
          ...styles.text,
          marginLeft: 6,
        }}
      >
        <Trans>
          Use remaining:{' '}
          {{
            amount: format(Math.abs(remaining), 'financial'),
          }}
        </Trans>
      </Text>
    </Button>
  );
}
